import React, { useState } from 'react';
import Header from '../Layout/Header';
import { useFirebase } from '../../hooks/useFirebase';
import './PrepareView.css';

const PrepareView: React.FC = () => {
  const { connectionStatus, participantCount, questions, setCurrentQuestion, resetSession, clearResponses, addQuestion, deleteQuestion } = useFirebase();
  const [newQuestionText, setNewQuestionText] = useState<string>('');
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleAddQuestion = async () => {
    if (newQuestionText.trim() && !isLoading) {
      setIsLoading(true);
      try {
        await addQuestion(newQuestionText);
        setNewQuestionText('');
      } catch (error) {
        console.error('Error adding question:', error);
        alert('Failed to add question. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!isLoading) {
      setIsLoading(true);
      try {
        await deleteQuestion(questionId);
        if (selectedQuestionId === questionId) {
          setSelectedQuestionId(null);
        }
      } catch (error) {
        console.error('Error deleting question:', error);
        alert('Failed to delete question. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSelectQuestion = (question: any) => {
    setSelectedQuestionId(question.id);
    
    // Clear responses first, then set new question
    if (connectionStatus === 'connected') {
      clearResponses();
      setCurrentQuestion(question.text);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleAddQuestion();
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the session? This will clear all responses and return participants to the initial state.')) {
      resetSession();
      setSelectedQuestionId(null); // Clear selected question in UI
    }
  };

  return (
    <div className="prepare-view">
      <Header
        showLogo={true}
        participantCount={participantCount}
        connectionStatus={connectionStatus}
        title="Question Preparation"
      />
      
      <main className="prepare-main">
        <div className="container">
          <div className="prepare-section">
            
            <div className="add-question-section">
              <h3>Add New Question</h3>
              <div className="question-input-group">
                <input
                  type="text"
                  value={newQuestionText}
                  onChange={(e) => setNewQuestionText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your question..."
                  className="question-input"
                  maxLength={200}
                />
                <button 
                  onClick={handleAddQuestion}
                  className="btn-primary add-question-button"
                  disabled={!newQuestionText.trim() || isLoading || connectionStatus !== 'connected'}
                >
                  {isLoading ? 'Adding...' : 'Add Question'}
                </button>
              </div>
              <div className="character-count">
                {newQuestionText.length}/200 characters
              </div>
            </div>

            <div className="questions-section">
              <h3>Question Library ({questions.length})</h3>
              
              {connectionStatus !== 'connected' ? (
                <div className="no-questions">
                  <p>Connecting to Firebase...</p>
                </div>
              ) : questions.length === 0 ? (
                <div className="no-questions">
                  <p>No questions yet. Add your first question above.</p>
                </div>
              ) : (
                <div className="questions-list">
                  {questions.map((question) => (
                    <div 
                      key={question.id} 
                      className={`question-item ${selectedQuestionId === question.id ? 'selected' : ''}`}
                    >
                      <div className="question-content">
                        <div className="question-text">{question.text}</div>
                        <div className="question-meta">
                          Added {new Date(question.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="question-actions">
                        <button
                          onClick={() => handleSelectQuestion(question)}
                          className="btn-primary select-question-button"
                          disabled={connectionStatus !== 'connected' || isLoading}
                        >
                          {selectedQuestionId === question.id ? 'Selected' : 'Use Question'}
                        </button>
                        <button
                          onClick={() => handleDeleteQuestion(question.id)}
                          className="btn-secondary delete-question-button"
                          disabled={isLoading}
                        >
                          {isLoading ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedQuestionId && (
              <div className="current-question-status">
                <h4>Current Question</h4>
                <div className="current-question-text">
                  {questions.find(q => q.id === selectedQuestionId)?.text}
                </div>
                <p className="status-text">
                  This question is now live. Participants will see it when they join or refresh.
                </p>
              </div>
            )}

            {/* Reset Session Section */}
            <div className="reset-session-section">
              <h3>Session Management</h3>
              <div className="reset-content">
                <div className="reset-description">
                  <p>Reset the current session to clear all participant responses and start fresh. 
                     Participants will return to the initial state and can respond again.</p>
                </div>
                <button 
                  onClick={handleReset}
                  className="reset-button"
                  disabled={connectionStatus !== 'connected'}
                >
                  🔄 Reset Session
                </button>
              </div>
            </div>

            <div className="prepare-navigation">
              <a href="/present" className="nav-link">
                Go to Presenter Dashboard →
              </a>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default PrepareView; 